import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { TimePicker as MuiTimePicker } from '@material-ui/pickers';
import { X } from 'lucide-react';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import { FormRow } from '../../../../../../components/Form/FormRow';
import {
  SelectTimeUnit,
  TimeUnit
} from '../../../../../../components/Time/SelectTimeUnit';
import {
  useTranslation,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { FlexContainer } from '../../../../../../layout/Flex';
import { ONE_DAY } from '../../../../../../services/time';
import { useTheme } from '../../../../../../themes';

export const DaySelector = React.memo(
  ({
    value,
    onChange,
    disabled,
    onBlur,
    zeroBased,
    error,
    suffix,
    mode = 'relative',
    convertToWeeks = true,
    newUx = false
  }: {
    value: number;
    onChange: (nextValue: number) => void;
    disabled?: boolean;
    onBlur?: () => void;
    zeroBased?: boolean;
    error?: boolean;
    suffix?: React.ReactNode;
    mode?: 'absolute' | 'relative';
    convertToWeeks?: boolean;
    // NEW_UX: render the compact (small) field height.
    newUx?: boolean;
  }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const basis = zeroBased ? 0 : 1;
    const days = Math.round(value / ONE_DAY);
    const [unit, setUnit] = useState<'d' | 'w'>(
      convertToWeeks && days !== 0 && days % 7 === 0 ? 'w' : 'd'
    );

    const numberValue =
      unit === 'w' ? Math.round(days / 7) + basis : days + basis;
    const dayStr =
      numberValue === 1 || mode === 'absolute' ? t('Day') : t('Days');
    const weekStr =
      numberValue === 1 || mode === 'absolute' ? t('Week') : t('Weeks');

    const inputField = (
      <TextField
        disabled={disabled}
        value={numberValue}
        type="number"
        inputProps={{
          min: basis
        }}
        error={error}
        size={newUx ? 'small' : undefined}
        style={{ width: theme.spacing(8) }}
        variant="outlined"
        onChange={(ev) => {
          const nextValue = +ev.target.value - basis;
          onChange(nextValue * ONE_DAY * (unit === 'w' ? 7 : 1));
        }}
        onBlur={onBlur}
      />
    );
    const selectField = (
      <Select
        variant="outlined"
        value={unit}
        disabled={disabled}
        style={{ width: theme.spacing(12) }}
        onChange={(ev) => {
          const nextUnit = ev.target.value as 'd' | 'w';
          const nextVal =
            (numberValue - basis) * ONE_DAY * (nextUnit === 'w' ? 7 : 1);
          onChange(nextVal);
          setUnit(nextUnit);
        }}
      >
        <MenuItem value="d">{dayStr}</MenuItem>
        <MenuItem value="w">{weekStr}</MenuItem>
      </Select>
    );

    return (
      <div>
        <FlexContainer spacing={1}>
          {mode === 'relative' && (
            <>
              {inputField}
              {selectField}
            </>
          )}
          {mode === 'absolute' && inputField}

          {suffix}
        </FlexContainer>
      </div>
    );
  }
);

export const Duration = React.memo(
  ({
    value,
    onChange,
    leftLabel,
    leftWidthMultiplier = 6,
    onBlur,
    error,
    helperText,
    zeroBased,
    suffix,
    max,
    fixedUnit
  }: {
    value: number;
    onChange: (nextValue: number) => void;
    leftLabel?: React.ReactNode;
    leftWidthMultiplier?: number;
    onBlur?: () => void;
    error?: boolean;
    helperText?: React.ReactNode;
    zeroBased?: boolean;
    suffix?: React.ReactNode;
    max?: number;
    fixedUnit?: 'd' | 'w';
  }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const basis = zeroBased ? 0 : 1;
    const days = Math.round(value / ONE_DAY);
    const [unit, setUnit] = useState<TimeUnit>(
      fixedUnit ?? (days !== 0 && days % 7 === 0 ? 'w' : 'd')
    );

    const numberValue = Math.max(
      basis,
      unit === 'w' ? Math.round(days / 7) : days
    );

    const maxValueInDays = max ? Math.round(max / ONE_DAY) : undefined;
    const maxValue = maxValueInDays
      ? unit === 'w'
        ? Math.floor(maxValueInDays / 7)
        : maxValueInDays
      : undefined;

    return (
      <div>
        <FlexContainer spacing={1}>
          <TextField
            value={numberValue}
            type="number"
            inputProps={{
              min: basis,
              max: maxValue
            }}
            error={error}
            style={{ width: theme.spacing(leftWidthMultiplier) }}
            variant="outlined"
            onChange={(ev) => {
              const nextValue = +ev.target.value;
              const unitsadjustedValue =
                nextValue * ONE_DAY * (unit === 'w' ? 7 : 1);
              const correctedNextValue = max
                ? Math.min(unitsadjustedValue, max)
                : unitsadjustedValue;
              onChange(correctedNextValue);
            }}
            onBlur={onBlur}
            label={leftLabel}
          />
          {fixedUnit ? (
            <Typography variant="body2">day</Typography>
          ) : (
            <SelectTimeUnit
              value={unit}
              plural={numberValue !== 1}
              onChange={(nextUnit) => {
                const nextVal =
                  numberValue * ONE_DAY * (nextUnit === 'w' ? 7 : 1);
                onChange(nextVal);
                setUnit(nextUnit);
              }}
              error={error}
            />
          )}
          {suffix}
        </FlexContainer>
        {helperText && (
          <FormHelperText error={!!error}>{helperText}</FormHelperText>
        )}
      </div>
    );
  }
);

export const TimePicker = ({
  t,
  minuteStep = 1,
  onChange,
  onClear,
  tz,
  autoWidth,
  width,
  // NEW_UX: render the compact (small) field height, matching DaySelector's own
  // newUx-gated size.
  newUx = false
}: {
  t: { hour: number; minute: number };
  minuteStep?: number;
  onChange: (nextT: { hour: number; minute: number }) => void;
  onClear?: () => void;
  tz?: string;
  autoWidth?: boolean;
  // Overrides the default tz/no-tz width below — needed when this instance's font
  // size doesn't match the size those defaults were tuned for.
  width?: number;
  newUx?: boolean;
}) => {
  const theme = useTheme();
  // construct a moment object which can be passed
  const v = useMemo(() => {
    return moment().hour(t.hour).minute(t.minute);
  }, [t]);

  return (
    <FlexContainer>
      <MuiTimePicker
        value={v}
        variant="inline"
        orientation="portrait"
        onChange={(nextV) => {
          nextV && onChange({ hour: nextV.hour(), minute: nextV.minute() });
        }}
        style={{ width: autoWidth ? 'auto' : width ?? (tz ? 130 : 100) }}
        size={newUx ? 'small' : undefined}
        inputVariant={newUx ? 'outlined' : undefined}
        ampm
        minutesStep={minuteStep}
        InputProps={
          tz
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <span style={{ color: theme.custom.colors.gray.w700 }}>
                      {moment().tz(tz).format('z')}
                    </span>
                  </InputAdornment>
                )
              }
            : undefined
        }
      />
      {!!onClear && (
        <IconButton color="primary" onClick={onClear}>
          <X size={16} />
        </IconButton>
      )}
    </FlexContainer>
  );
};

export const RepeatSelector = React.memo(
  ({
    value,
    onChange,
    disabled
  }: {
    value: { enabled: boolean; frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' };
    onChange: (nextValue: {
      enabled: boolean;
      frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
    }) => void;
    disabled?: boolean;
  }) => {
    const { t } = useUnsafeTranslation();
    return (
      <FormRow>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={value.enabled}
              onChange={(ev) => {
                onChange({
                  ...value,
                  enabled: ev.target.checked
                });
              }}
              disabled={disabled}
            />
          }
          label="Repeat"
        />
        <Select
          variant="outlined"
          value={value.frequency}
          fullWidth={true}
          disabled={disabled || !value.enabled}
          onChange={(ev) => {
            const nextValue = ev.target.value as 'DAILY' | 'WEEKLY' | 'MONTHLY';
            onChange({
              ...value,
              frequency: nextValue
            });
          }}
        >
          <MenuItem value="DAILY">{t('Daily')}</MenuItem>
          <MenuItem value="WEEKLY">{t('Weekly')}</MenuItem>
          <MenuItem value="MONTHLY">{t('Monthly')}</MenuItem>
        </Select>
      </FormRow>
    );
  }
);
